/**
 * spec グラフのノード型語彙。
 *
 * 11 型の閉集合は「ノード・エッジ仕様」§1 が正。ここに無い型は表現できない
 * （語彙の追加は cortex への PR = 契約変更でのみ行う — 制約 1）。
 *
 * `EXTERNAL_NODE_TYPES` は spec グラフが参照するだけで所有しないノード型で、
 * 実体は db-graph（Table / Column）と code-graph = SPG（Api / Task）にある。
 * spec グラフ内にノードを複製せず、qualifiedName 規約で横断 JOIN する。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business spec グラフの 11 ノード型 allowlist と、外部グラフが所有する参照先ノード型の定義
 * @graph-connects none
 */
/**
 * spec グラフが所有するノード型 11 種（NE §1。並びは同節の表の順）。
 *
 * 粒度の 4 段（Stage / Flow / Business / Rule）と、どの段からも引かれる
 * 横串（Term / Verb / Unit / Actor / Domain）、画面の 2 型（Screen / Component）で閉じる。
 * 段を型で固定するのは、どこまで書くかを書き手ごとの裁量にしないためである（NE §0 粒度の段）。
 * ステージの並び（鎖）はノードにせず、ステージの入口の参照から導出する（`deriveStageChains`）。
 *
 * **PJ はここに居ない。** 一度は `Pj` としてこの語彙に置いた — PJ が文字列の列
 * でしかなかったために実在を保証できず、動作確認で入れた番号がそのまま本番の変更ログに
 * 残ったからである。だが実在確認も一覧も詳細も、その PJ の変更行を見れば答えられる。
 * ノードに置くと、変更ログを PJ で分割しても状態だけは別表へ取りに行くことになり、
 * 「すべてのノードに効く規則」からも毎回 1 型だけ除外し続ける必要があった。PJ は変更ログの
 * 行になった（`PJ_TARGET_KIND`）。この語彙は仕様の中身だけを持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business spec グラフの 11 ノード型の閉集合。BQ の nodeType 列に入る値の allowlist
 * @graph-connects none
 */
export const SPEC_NODE_TYPES = [
    'Stage',
    'Flow',
    'Business',
    'Rule',
    'Term',
    'Verb',
    'Unit',
    'Actor',
    'Domain',
    'Screen',
    'Component',
];
/**
 * 分類マスターの 5 型（Actor / Domain / Stage / Verb / Unit。NE §1-subsets）。
 *
 * 件数がほぼ固定で、書き手が増やすと分類や語彙が割れて意味を失う型である。一般編集の
 * 一般の書込ツールからは触れず spec MCP の `manage_spec_masters` に分離する（API §4.4・決定 y-5）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business manage_spec_masters が扱うマスター 5 型の allowlist
 * @graph-connects none
 */
export const MASTER_NODE_TYPES = ['Actor', 'Domain', 'Stage', 'Verb', 'Unit'];
/**
 * 変更に承認が要るノード型（Term / Stage / Actor / Domain。NE §1-subsets、制約 31）。
 *
 * 用語は業務名の目的語に、ステージ・アクター・ドメインは業務の分類そのものになる。
 * どの型の変更も、未承認が残る PJ は確定できない — 書き手（AI を含む）が語彙や分類ごと分身業務を
 * 作れないようにするためである。Domain は仕様オーナーの決定（2026-09-27 10:40「ドメインは承認を
 * 通すので MCP で制限しない」）で入れている。設計書 NE §1 の承認対象は Domain を入れていないが、
 * この決定がまだ書き足されていない行である。Verb と Unit は入れない — 書けるのが管理者だけなので、
 * 承認を関門に置く理由が無い。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 変更に管理者の承認が要るノード型（Term / Stage / Actor / Domain）の allowlist
 * @graph-connects none
 */
export const APPROVAL_TARGET_NODE_TYPES = ['Term', 'Stage', 'Actor', 'Domain'];
/**
 * 一般の書込ツールで書け、消せるノード型 6 種（Term / Flow / Business / Rule / Screen /
 * Component。NE §1-subsets）。マスター 5 型は管理者専用の経路に置くので入れない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 一般の書込ツールが書き・消せるノード型 6 種の allowlist（マスターを除く）
 * @graph-connects none
 */
export const EDITABLE_NODE_TYPES = [
    'Term',
    'Flow',
    'Business',
    'Rule',
    'Screen',
    'Component',
];
/**
 * spec グラフが参照するだけで所有しない外部グラフのノード型。
 *
 * Table / Column は db-graph、Api / Task は code-graph（SPG）が実体を持つ。
 * `USES_TABLE` / `USES_COLUMN` / `IMPLEMENTED_BY_API` / `IMPLEMENTED_BY_TASK` の
 * target 側に現れる（NE §2）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business エッジの target 側に現れる外部グラフ所有ノード型（db-graph / SPG）の allowlist
 * @graph-connects none
 */
export const EXTERNAL_NODE_TYPES = ['Table', 'Column', 'Api', 'Task'];
//# sourceMappingURL=node-types.js.map